import React, { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import {
  initializeMobilePayment, initializeBankTransfer,
  verifyMobilePayment, verifyBankTransfer,
  pollPaymentConfirmation, generateChargeId,
  MOBILE_OPERATORS, PAYMENT_METHODS,
} from "../services/paychangu";
import { reserveTickets, releaseTickets, finalizeTicketPurchase, calcTicketTotal } from "../services/ticketing";
import { getEvent } from "../firebase/firestoreService";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebase/config";
import "../styles/checkout.css";
import "../styles/events.css";

const fmt = (n) => `MK ${Number(n || 0).toLocaleString()}`;

export default function TicketCheckout({ cartCount, onCartClick }) {
  const { eventId } = useParams();
  const location = useLocation();
  const navigate  = useNavigate();
  const { user }  = useAuth();
  const isPhoneUser = user?.providerData?.[0]?.providerId === "password";

  const selections = location.state?.selections || [];
  const [event, setEvent] = useState(null);

  const [details, setDetails] = useState({ firstName: "", lastName: "", phone: "", email: "" });
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [operator,      setOperator]      = useState(null);
  const [paying,    setPaying]    = useState(false);
  const [payStatus, setPayStatus] = useState("idle");
  const [error,     setError]     = useState("");
  const [bankDetails, setBankDetails] = useState(null);

  useEffect(() => {
    if (!selections.length) { navigate(`/events/${eventId}`); return; }
    getEvent(eventId).then(setEvent);
  }, [eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user) return;
    if (!isPhoneUser) {
      const parts = (user.displayName || "").split(" ");
      setDetails({ firstName: parts[0] || "", lastName: parts.slice(1).join(" ") || "", email: user.email || "", phone: "" });
      return;
    }
    db.collection("users").doc(user.uid).get().then((doc) => {
      if (!doc.exists) return;
      const d = doc.data();
      setDetails({ firstName: d.firstName || "", lastName: d.lastName || "", phone: d.phone || "", email: d.email || "" });
    });
  }, [user, isPhoneUser]);

  const set = (k, v) => setDetails((d) => ({ ...d, [k]: v }));

  const subtotal = selections.reduce((s, sel) => s + sel.price * sel.qty, 0);
  const { fee, total } = calcTicketTotal(subtotal);
  const totalQty = selections.reduce((s, sel) => s + sel.qty, 0);

  const canPay =
    details.firstName.trim() && details.lastName.trim() && details.phone.trim() &&
    paymentMethod && (paymentMethod === "bank_transfer" || operator);

  const handlePay = async () => {
    if (!canPay || !event) return;
    setError("");
    setPaying(true);
    setPayStatus("idle");
    setBankDetails(null);

    try {
      await reserveTickets(eventId, selections);
    } catch (e) {
      setError(e.message || "Some tickets are no longer available.");
      setPaying(false);
      return;
    }

    const chargeId = generateChargeId();
    const buyer = { name: `${details.firstName} ${details.lastName}`, email: details.email, phone: details.phone };
    const eventDateIso = event.eventDate?.toDate ? event.eventDate.toDate().toISOString() : new Date(event.eventDate).toISOString();

    const finalize = async () => {
      const result = await finalizeTicketPurchase({
        chargeId, paymentMethod, eventId, eventName: event.name, eventDate: eventDateIso, buyer, selections,
      });
      setPaying(false);
      if (!result.success) {
        // Payment succeeded but issuance failed — do NOT release the reservation,
        // the customer was charged. Point them to support with the reference.
        setError(`Payment received but tickets couldn't be issued automatically. Contact us with reference ${chargeId}. (${result.error})`);
        return;
      }
      navigate("/tickets/success", { state: { tickets: result.tickets, eventName: event.name, chargeId } });
    };

    if (paymentMethod === "mobile_money") {
      const result = await initializeMobilePayment({
        firstName: details.firstName, lastName: details.lastName,
        email: details.email, phone: details.phone, amount: total, chargeId, operator,
      });
      if (!result.success) {
        await releaseTickets(eventId, selections);
        setError(typeof result.error === "string" ? result.error : "Payment initialization failed.");
        setPaying(false);
        return;
      }

      setPayStatus("waiting");
      const poll = await pollPaymentConfirmation(chargeId, verifyMobilePayment, { timeoutMs: 120000 });
      if (!poll.confirmed) {
        await releaseTickets(eventId, selections);
        setPaying(false);
        setPayStatus(poll.reason === "timeout" ? "timeout" : "failed");
        setError(poll.reason === "timeout" ? "Payment timed out. Please try again." : "Payment was declined or cancelled. Please try again.");
        return;
      }
      await finalize();
    }

    if (paymentMethod === "bank_transfer") {
      const result = await initializeBankTransfer({ amount: total, chargeId });
      if (!result.success) {
        await releaseTickets(eventId, selections);
        setError(typeof result.error === "string" ? result.error : "Bank transfer initialization failed.");
        setPaying(false);
        return;
      }

      const { success: _s, raw: _r, ...cleanResult } = result;
      setBankDetails({ ...cleanResult, chargeId });
      setPayStatus("waiting");
      setPaying(false);

      const poll = await pollPaymentConfirmation(chargeId, verifyBankTransfer, { timeoutMs: 600000 });
      if (poll.confirmed) {
        await finalize();
      } else {
        await releaseTickets(eventId, selections);
        setPayStatus(poll.reason === "timeout" ? "timeout" : "failed");
        setError("Transfer not received within the time limit. Contact us with your reference: " + chargeId);
      }
    }
  };

  const selectedOp = MOBILE_OPERATORS.find((o) => o.id === operator);

  const payLabel = () => {
    if (payStatus === "waiting" && paymentMethod === "mobile_money") return "⏳ Waiting for PIN…";
    if (payStatus === "waiting" && paymentMethod === "bank_transfer") return "⏳ Waiting for transfer…";
    if (paying) return "Initializing…";
    if (paymentMethod === "mobile_money" && selectedOp) return `Make Payment with ${selectedOp.label}`;
    return "Make Payment";
  };

  if (!selections.length) return null;

  return (
    <div className="checkout">
      <Navbar cartCount={cartCount || 0} onCartClick={onCartClick || (() => {})} />

      <div className="checkout__main">
        <div className="checkout__summary">
          <h3 className="checkout__summary-title">Order Summary</h3>
          {selections.map((sel) => (
            <div key={sel.ticketTypeId} className="checkout__summary-row">
              <span>{sel.name} × {sel.qty}</span>
              <span>{fmt(sel.price * sel.qty)}</span>
            </div>
          ))}
          <hr className="checkout__summary-divider" />
          <div className="checkout__summary-row">
            <span>Subtotal</span><span>{fmt(subtotal)}</span>
          </div>
          <div className="checkout__summary-row">
            <span>Service fee</span><span>{fmt(fee)}</span>
          </div>
          <div className="checkout__summary-total">
            <span>Total ({totalQty} ticket{totalQty !== 1 ? "s" : ""})</span><span>{fmt(total)}</span>
          </div>

          <button className="checkout__pay-btn" onClick={handlePay} disabled={!canPay || paying}>
            {payLabel()}
          </button>
          {!canPay && <p className="checkout__pay-hint">Fill in your details and choose a payment method</p>}

          {payStatus === "waiting" && paymentMethod === "mobile_money" && (
            <p style={{ fontSize: 12, color: "#7C5C4A", textAlign: "center", marginTop: 10,
              background: "#fdf3ec", borderRadius: 8, padding: "10px 14px", lineHeight: 1.6 }}>
              📱 Prompt sent to <strong>{details.phone}</strong>.<br />Enter your PIN to confirm.
            </p>
          )}
          <p className="checkout__pay-note">Secured by Paychangu</p>
        </div>

        <div>
          <h1 className="checkout__title">{event ? event.name : "Ticket Checkout"}</h1>

          <div className="checkout__section">
            <div className="checkout__section-header"><p className="checkout__section-title">Your Details</p></div>
            <div className="checkout__form-row">
              <div className="checkout__form-group">
                <label className="checkout__label">First Name</label>
                <input className="checkout__input" value={details.firstName} onChange={(e) => set("firstName", e.target.value)} />
              </div>
              <div className="checkout__form-group">
                <label className="checkout__label">Last Name</label>
                <input className="checkout__input" value={details.lastName} onChange={(e) => set("lastName", e.target.value)} />
              </div>
            </div>
            <div className="checkout__form-group">
              <label className="checkout__label">Phone Number</label>
              <input className="checkout__input" value={details.phone} onChange={(e) => set("phone", e.target.value)} placeholder="e.g. 0991234567" />
            </div>
            <div className="checkout__form-group">
              <label className="checkout__label">Email</label>
              <input className="checkout__input" value={details.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" />
            </div>
          </div>

          <div className="checkout__section">
            <div className="checkout__section-header"><p className="checkout__section-title">Payment Method</p></div>
            <div className="checkout__operator-options" style={{ marginBottom: 16 }}>
              {PAYMENT_METHODS.map((m) => (
                <button key={m.id}
                  onClick={() => { setPaymentMethod(m.id); setOperator(null); }}
                  className={`checkout__operator-pill ${paymentMethod === m.id ? "checkout__operator-pill--active" : ""}`}>
                  <span className={`checkout__operator-icon checkout__operator-icon--${m.id}`}>
                    {m.id === "mobile_money" ? "📱" : "🏦"}
                  </span>
                  <span>
                    <span style={{ display: "block", fontWeight: 500 }}>{m.label}</span>
                    <span style={{ fontSize: 10, opacity: .7 }}>{m.sub}</span>
                  </span>
                  {paymentMethod === m.id && <span className="checkout__operator-check">✓</span>}
                </button>
              ))}
            </div>

            {paymentMethod === "mobile_money" && (
              <div className="checkout__operator-options">
                {MOBILE_OPERATORS.map((op) => (
                  <button key={op.id}
                    onClick={() => setOperator(op.id)}
                    className={`checkout__operator-pill ${operator === op.id ? "checkout__operator-pill--active" : ""}`}>
                    <span className={`checkout__operator-icon checkout__operator-icon--${op.id}`}>
                      {op.id === "airtel" ? "A" : "T"}
                    </span>
                    {op.label}
                    {operator === op.id && <span className="checkout__operator-check">✓</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          {error && (
            <div style={{
              background: "#fde8e8", border: "1px solid #f5c6c6",
              borderRadius: 8, padding: "12px 16px", fontSize: 13,
              color: "#c0392b", marginTop: 8,
            }}>
              ⚠️ {error}
            </div>
          )}
        </div>
      </div>

      {bankDetails && (
        <div className="checkout-popup-overlay" onClick={() => {}}>
          <div className="checkout-popup" onClick={(e) => e.stopPropagation()}>
            <div className="checkout-popup__header">
              <h3 className="checkout-popup__title">🏦 Transfer Payment Details</h3>
            </div>
            <div className="checkout-popup__body">
              <p style={{ fontSize: 13, color: "#555", marginBottom: 16, lineHeight: 1.6 }}>
                Transfer exactly <strong>{fmt(total)}</strong> to the account below. Your tickets are issued automatically once received.
              </p>
              {[
                { label: "Bank Name",      value: bankDetails.bankName },
                { label: "Account Number", value: bankDetails.accountNumber, mono: true },
                { label: "Account Name",   value: bankDetails.accountName },
              ].map(({ label, value, mono }) => (
                <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: "1px solid #f5f2ee" }}>
                  <span style={{ fontSize: 11, letterSpacing: "1px", textTransform: "uppercase", color: "#999" }}>{label}</span>
                  <span style={{ fontSize: 14, fontFamily: mono ? "monospace" : "inherit", fontWeight: 500 }}>{value}</span>
                </div>
              ))}
              <p style={{ fontSize: 12, color: "#7C5C4A", marginTop: 14, lineHeight: 1.6, background: "#fdf3ec", borderRadius: 8, padding: "10px 14px" }}>
                ⏳ Waiting for your transfer… This page will update automatically once payment is received.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
